import React from 'react';
import type { PatientRecord } from '../../patients/types';
import type { TpPatientContext, TpTreatmentPitch } from '../../patient-treatment/types';
import { Avatar } from '../Avatar/Avatar';

export interface PatientTreatmentStripProps {
  patient: PatientRecord;
  context: TpPatientContext;
  /** The sell sequence, so the header numbers are its sum (amendment 27 §7). */
  treatments?: TpTreatmentPitch[];
}

export function PatientTreatmentStrip({ patient, context, treatments = [] }: PatientTreatmentStripProps) {
  const { insurance } = context;
  const planValue = treatments.reduce((n, t) => n + t.fee, 0) || context.unscheduledValue;
  const hePays = treatments.reduce((n, t) => n + t.patientOOP, 0);
  const procedureCount = treatments.length;

  return (
    <section className="tc-rise tc-d0 mb-[26px]">
      <span className="tc-eyebrow mb-2 block">Treatment plan · TC cheat sheet</span>
      <div className="flex flex-wrap items-end gap-5">
        <div className="flex min-w-[300px] flex-1 items-center gap-4">
          <Avatar
            name={patient.name}
            src={patient.avatarUrl}
            initials={patient.initials}
            hue="var(--ai-3)"
            size={54}
          />
          <div className="min-w-0">
            <h2 className="m-0 font-[var(--font-display)] text-[36px] font-semibold leading-none tracking-[-0.035em] text-[var(--ink-0)]">
              {patient.first}{' '}
              <span className="font-normal text-[var(--ink-2)]">{patient.last}</span>
            </h2>
            {/* The header line (staging fix 29 §5): age · since · visits · phone ·
                carrier · language. No next-visit hygiene line here. */}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-[var(--ink-3)]">
              <span className="whitespace-nowrap">{patient.age} · patient since {patient.since}</span>
              <span className="text-[var(--ink-4)]">·</span>
              <span className="tc-mono whitespace-nowrap">{patient.visits} visits</span>
              <span className="text-[var(--ink-4)]">·</span>
              <span className="tc-mono whitespace-nowrap">{patient.phone}</span>
              <span className="text-[var(--ink-4)]">·</span>
              <span className="whitespace-nowrap">{insurance.carrier}</span>
              {patient.languageCode === 'fa' ? (
                <>
                  <span className="text-[var(--ink-4)]">·</span>
                  <span className="whitespace-nowrap">speaks Farsi</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
        {/* Plan value / He pays / Benefits left (amendment 27 §7). The old
            headers (Unscheduled value / Benefit remaining) didn't add up to the
            sell sequence; these are computed from it. */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="text-right">
            <span className="tc-eyebrow mb-0.5 block whitespace-nowrap">Plan value</span>
            <div className="tc-mono text-[23px] font-semibold tracking-[-0.01em] text-[var(--success)]">
              ${planValue.toLocaleString()}
            </div>
            <div className="mt-px text-[10.5px] text-[var(--ink-4)]">{procedureCount} procedures</div>
          </div>
          <div className="h-9 w-px bg-[var(--cream-2)]" />
          <div className="text-right">
            <span className="tc-eyebrow mb-0.5 block whitespace-nowrap">He pays</span>
            <div className="tc-mono text-[19px] font-semibold text-[var(--ink-0)]">
              ~${hePays.toLocaleString()}
            </div>
            <div className="mt-px text-[10.5px] text-[var(--ink-4)]">after insurance</div>
          </div>
          <div className="h-9 w-px bg-[var(--cream-2)]" />
          <div className="text-right">
            <span className="tc-eyebrow mb-0.5 block whitespace-nowrap">Benefits left</span>
            <div className="tc-mono text-[19px] font-semibold text-[var(--ink-0)]">
              ${insurance.remaining.toLocaleString()}
            </div>
            <div className="mt-px text-[10.5px] text-[var(--ink-4)]">
              {insurance.resetsIn === '—' ? 'No plan on file' : `resets in ${insurance.resetsIn}`}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
